import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { newGetNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../../services/userService';
import NoData from '../../../components/common/NoData';
import moment from 'moment-timezone';
 
function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Intersection Observer ref
  const observerRef = useRef();
  const lastNotificationRef = useRef();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchNotifications = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await newGetNotifications({
        page,
        limit: 20,
        search: debouncedSearchTerm
      });

      // console.log('Notifications response:', response);
      // console.log('Response type:', typeof response);
      // console.log('Response keys:', response ? Object.keys(response) : 'response is null/undefined');
      // Removed multi-line console.log,
      //   notificationsLength: response?.notifications?.length,
      //   pagination: response?.pagination
      // });

      if (response && response.notifications) {
        const newNotifications = response.notifications || [];
        const pagination = response.pagination;

        if (append) {
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }
        
        // Removed multi-line console.log

        setHasMore(pagination?.hasNextPage || false);
        setTotalCount(pagination?.totalCount || 0);
        setCurrentPage(page);
      } else {
        if (!append) {
          setNotifications([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearchTerm]);

  // Fetch notifications on mount and when search changes
  useEffect(() => {
    // console.log('useEffect triggered - debouncedSearchTerm:', debouncedSearchTerm);
    setCurrentPage(1);
    setHasMore(true);
    fetchNotifications(1, false);
  }, [debouncedSearchTerm]);

  // Load more notifications
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchNotifications(currentPage + 1, true);
    }
  }, [loadingMore, hasMore, currentPage, fetchNotifications]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;

    if (lastNotificationRef.current) {
      observer.observe(lastNotificationRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMore]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleNotificationClick = async (notification) => {
    try {
      const response = await markNotificationAsRead({ notificationId: notification.id });

      if (response.success) {
        const updatedNotifications = notifications.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        );
        setNotifications(updatedNotifications);
      }

      navigate(`/admin/notifications/notificationDetails`, {
        state: { notification }
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
      navigate(`/admin/notifications/notificationDetails`, {
        state: { notification }
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await markAllNotificationsAsRead();

      if (response.success) {
        const updatedNotifications = notifications.map(notification => ({
          ...notification,
          is_read: true
        }));
        setNotifications(updatedNotifications);
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  if (loading && currentPage === 1) {
    return (
      <div>
        <div className="text-center p-5">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading notifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="text-center p-5 text-red-500">
          {error}
        </div>
      </div>
    );
  }

  // Removed multi-line console.log

  return (
    <>
      <div className="flex items-center my-2 flex-col md:flex-row gap-3">
        <div className="w-full md:w-1/3">
          <div className="relative">
            <Icon
              icon="fluent:search-20-regular"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            />
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
              placeholder="Search notifications..."
              aria-label="Search notifications"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>
        <div className="w-full md:w-2/3 flex justify-end mt-2 md:mt-0">
          <button
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            onClick={handleMarkAllAsRead}
            disabled={!notifications.some(n => !n.is_read)}
          >
            <Icon icon="mdi:check-all" width="18" height="18" />
            <span>Mark all as read</span>
          </button>
        </div>
      </div>

      <div className="flex">
        <div className="w-full">
          <div className="p-0 md:p-2">
            <div>
              {notifications.length > 0 ? (
                notifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    ref={index === notifications.length - 1 ? lastNotificationRef : null}
                    className={`bg-white rounded-lg shadow-sm border border-gray-100 p-4 mb-3 cursor-pointer hover:shadow-md transition-shadow ${!notification.is_read ? 'border-l-4 border-l-primary' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                    role="button"
                    tabIndex={0}
                  >
                    <div>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold">{notification.title}</h3>
                        {!notification.is_read && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">New</span>
                        )}
                      </div>
                      <p className="text-gray-700 mb-2">{notification.body}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Icon icon="mdi:clock-outline" width="16" height="16" />
                        <span>{moment.utc(notification.createdAt).local().format('MMM DD, YYYY, hh:mm A')}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message={searchTerm ? "No notifications found matching your search." : "No notifications found."} />
              )}
              
              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="text-center py-3">
                  <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent" role="status">
                    <span className="sr-only">Loading more...</span>
                  </div>
                  <span className="ml-2">Loading more notifications...</span>
                </div>
              )}
              
              {/* End of List Indicator */}
              {!hasMore && notifications.length > 0 && (
                <div className="text-center py-3">
                  <p className="text-gray-500 mb-0">
                    <Icon icon="mdi:check-circle" className="mr-2" />
                    You've seen all notifications
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Notifications;
